/*
 * Routing.
 *
 * The URL shape follows from the data model: kb.Article.Slug is unique only
 * WITHIN a product (UQ_Article_ProductId_Slug), so a slug on its own addresses
 * nothing and /kb/{productSlug}/{slug} is the whole address. The product segment
 * is not decoration: two products may each carry a "getting-started", and the
 * pair is the only thing that tells the two pages apart.
 *
 * /kb/id/{articleId} stays alongside it as the permalink. Both halves of the
 * readable address are editable - an article can be re-slugged and it can be
 * moved to another product - and an id is neither, so a link written against the
 * id survives both. It is also what the article picker in the editor inserts.
 *
 * WHY THE THREE CANNOT COLLIDE. React Router ranks a static segment above a
 * dynamic one and a splat below both, so /kb/id/xyz matches the permalink rather
 * than the pair, and everything else with two segments matches the pair. The
 * ranking is not the whole guard: a product whose slug was "id" would make
 * /kb/id/{articleSlug} a legitimate article address that the permalink outranks,
 * which is why the shared product schema refuses that slug outright (see
 * RESERVED_PRODUCT_SLUGS). Routing ambiguity is prevented where products are
 * named, not patched up here.
 *
 * WHY THE CATCH-ALL IS INSIDE PublicLayout. Every link written before the
 * address gained a product segment is one segment long, and those links are dead
 * by decision: /kb/{slug} is not redirected and not guessed at, because a slug
 * can now belong to several articles under different products and picking one
 * would take a reader to the wrong page while looking like it had worked. What a
 * dead link must NOT do is strand the reader on a bare page, so kb/* renders the
 * not-found body inside the public chrome - header, footer, a way back - exactly
 * like every other reader page. The outer "*" keeps the standalone shell,
 * because a URL under none of /kb, /releasenotes and /admin has no layout to
 * belong to.
 *
 * RELEASE NOTES (S42, 2026-10-12) sit beside the articles under the owner's
 * prefix: /releasenotes is the list, and /releasenotes/{productSlug}/{version}
 * is one note - the pair is the whole address, because there is one note per
 * product and version (UQ_ReleaseNote_ProductId_Version) and a note has no slug.
 * A dotted version such as v1.0.87.3 is an ordinary dynamic segment to the
 * router. releasenotes/* is the same in-chrome not-found as kb/*, for a path with
 * one segment or three. Every link to these pages is built by the shared
 * releaseNoteUrls.ts; the route patterns below are written out relative, as the
 * kb ones are, and App.test pins that the addresses that module builds land on
 * them.
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import { PublicLayout } from './public/PublicLayout.js';
import { BrowsePage } from './public/BrowsePage.js';
import { ArticlePage } from './public/ArticlePage.js';
import { ArticleByIdPage } from './public/ArticleByIdPage.js';
import { ReleaseNotesPage } from './public/ReleaseNotesPage.js';
import { ReleaseNotePage } from './public/ReleaseNotePage.js';
import { NotFoundContent, NotFoundPage } from './public/NotFoundPage.js';
import { AdminLayout } from './admin/AdminLayout.js';
import { DashboardPage } from './admin/DashboardPage.js';
import { ArticleListPage } from './admin/ArticleListPage.js';
import { ArticleEditorPage } from './admin/ArticleEditorPage.js';
import { NewArticlePage } from './admin/NewArticlePage.js';
import { ReleaseNoteListPage } from './admin/ReleaseNoteListPage.js';
import { NewReleaseNotePage } from './admin/NewReleaseNotePage.js';
import { ReleaseNoteEditorPage } from './admin/ReleaseNoteEditorPage.js';
import { MetadataPage } from './admin/MetadataPage.js';
import { PromotedArticlesPage } from './admin/PromotedArticlesPage.js';
import { FrequentQuestionsPage } from './admin/FrequentQuestionsPage.js';
import { SettingsPage } from './admin/SettingsPage.js';
import { AttachmentsPage } from './admin/AttachmentsPage.js';

export function App(): JSX.Element {
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route index element={<BrowsePage />} />
        <Route path="kb" element={<Navigate to="/" replace />} />
        <Route path="kb/id/:articleId" element={<ArticleByIdPage />} />
        <Route path="kb/:productSlug/:slug" element={<ArticlePage />} />
        <Route path="kb/*" element={<NotFoundContent />} />
        <Route path="releasenotes" element={<ReleaseNotesPage />} />
        <Route path="releasenotes/:productSlug/:version" element={<ReleaseNotePage />} />
        <Route path="releasenotes/*" element={<NotFoundContent />} />
      </Route>

      <Route path="admin" element={<AdminLayout />}>
        <Route index element={<DashboardPage />} />
        <Route path="articles" element={<ArticleListPage />} />
        <Route path="articles/new" element={<NewArticlePage />} />
        <Route path="articles/:articleId" element={<ArticleEditorPage />} />
        {/* The static "new" segment outranks the id, as articles/new does. */}
        <Route path="releasenotes" element={<ReleaseNoteListPage />} />
        <Route path="releasenotes/new" element={<NewReleaseNotePage />} />
        <Route path="releasenotes/:releaseNoteId" element={<ReleaseNoteEditorPage />} />
        <Route path="attachments" element={<AttachmentsPage />} />
        <Route path="promoted" element={<PromotedArticlesPage />} />
        <Route path="questions" element={<FrequentQuestionsPage />} />
        <Route path="metadata" element={<MetadataPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
